import YouMightLike from "@/components/YouMightLike";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { SupplierImg } from "@/components/SupplierImg";
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { pushRecent } from "@/lib/recentlyViewed";
import { api, assetUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/Stars";
import { ProductCard } from "@/components/ProductCard";
import { AgeGate, ComplianceNotice, isAgeVerified } from "@/components/AgeGate";
import { WeightNote, QtyStepper } from "@/components/QtyStepper";
import { unitWeightKg } from "@/lib/weight";
import { LiveViewers } from "@/components/LiveViewers";
import { jmd } from "@/lib/format";
import { Price } from "@/components/Price";
import {
  Heart, ShoppingCart, Zap, Truck, MapPin, ShieldCheck, RotateCcw, Store, Check,
  Loader2, Package, Play, Scale, Flag, Bell, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { ArtDepositButton } from "@/components/ArtDeposit";
import { PriceRequestForm, AvailabilityBadge } from "@/components/PriceRequest";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWish, inWishlist } = useCart();
  const { toggleCompare, inCompare } = useCompare();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("desc");
  const [qty, setQty] = useState(0);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewVideo, setReviewVideo] = useState("");
  const [upBusy, setUpBusy] = useState(false);
  const [ageOk, setAgeOk] = useState(isAgeVerified());
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [enqSent, setEnqSent] = useState(false);
  const [enq, setEnq] = useState({ name: "", email: "", phone: "", viewing_date: "", message: "" });
  const [research, setResearch] = useState(null);
  const [researchBusy, setResearchBusy] = useState(false);
  const [researchErr, setResearchErr] = useState("");

  const handleRestock = async () => {
    let email = user?.email;
    if (!email) { email = window.prompt("Enter your email and we'll alert you the moment it's back in stock:"); if (!email) return; }
    try {
      const { data: r } = await api.post(`/products/${data.product.id}/restock-alert`, { email });
      toast.success(r.already_in_stock ? "Good news — it's already back in stock!" : "Done! We'll email you the moment it's back.");
    } catch { toast.error("Could not set up the alert"); }
  };

  const runResearch = async () => {
    if (research || researchBusy) return;
    setResearchBusy(true); setResearchErr("");
    try {
      const { data: r } = await api.post("/ai/research", { product_id: data.product.id });
      setResearch(r);
    } catch (e) {
      setResearchErr(e?.response?.data?.detail || "AI research is unavailable right now.");
    } finally { setResearchBusy(false); }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => {
      setData(data); setActive(0); pushRecent(data.product);
      if (user && data.product?.category) api.post("/me/view", { category: data.product.category }).catch(() => {});
    }).catch((e) => { if (e.response?.status === 404) navigate("/search", { replace: true }); else toast.error("Could not load this product — please try again"); });
  }, [id]);

  if (!data) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-emerald" size={32} /></div>;
  const p = data.product;
  if (p.age_restricted && !ageOk) return <AgeGate onVerified={() => setAgeOk(true)} />;
  const gallery = [p.image, ...(p.images || [])].slice(0, 4).map(assetUrl);

  const submitReview = async () => {
    if (!comment.trim()) return;
    await api.post("/reviews", { product_id: id, rating, comment, author: user ? user.name : "Guest Shopper", video_url: reviewVideo || null });
    toast.success("Review submitted!");
    setComment(""); setReviewVideo("");
    const { data } = await api.get(`/products/${id}`);
    setData(data);
  };

  const uploadReviewVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error("Please sign in to attach a video"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReviewVideo(data.url);
      toast.success("Video attached to your review");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed (video max 50MB)"); }
    setUpBusy(false);
  };

  const reportListing = async () => {
    const reason = window.prompt("Why are you reporting this listing? (e.g. counterfeit, wrong item, offensive, pricing)");
    if (!reason || !reason.trim()) return;
    try {
      await api.post("/trust/report", { product_id: id, reason: reason.trim(), details: "" });
      toast.success("Thanks — our Trust & Safety team will review this.");
    } catch { toast.error("Could not submit report"); }
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!enq.name.trim() || !enq.email.trim() || !enq.phone.trim()) { toast.error("Please fill name, email and phone"); return; }
    try {
      await api.post("/enquiries", { product_id: id, ...enq });
      setEnqSent(true);
      toast.success("Enquiry sent — our brokers will contact you shortly.");
    } catch { toast.error("Could not send enquiry"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-sm text-muted-foreground mb-4">
        <Link to="/" className="hover:text-emerald">Home</Link> / <Link to={`/category/${p.category}`} className="hover:text-emerald capitalize">{p.category.replace("-", " ")}</Link> / <span className="text-ink">{p.name}</span>
      </nav>
      <div className="flex justify-end mb-2">
        <button onClick={reportListing} className="text-xs text-muted-foreground hover:text-coral flex items-center gap-1" data-testid="report-listing-btn"><Flag size={13} /> Report this listing</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 min-w-0 overflow-x-hidden">
        <div>
          <div className="relative rounded-3xl overflow-hidden bg-slate-100 aspect-square border border-border">
            {active === 0 ? <SupplierImg product={p} alt={p.name} className="h-full w-full object-cover" /> : <img src={gallery[active]} alt={p.name} className="h-full w-full object-cover" />}
            {p.discount > 0 && <span className="absolute top-4 left-4 bg-coral text-white text-sm font-bold px-3 py-1.5 rounded-full">-{p.discount}%</span>}
          </div>
          <div className="flex gap-3 mt-3">
            {gallery.map((g, i) => (
              <button key={`${g}-${i}`} onClick={() => setActive(i)} className={`h-20 w-20 rounded-xl overflow-hidden border-2 ${i === active ? "border-emerald" : "border-transparent"}`}>
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
            {p.video && (
              <a href={p.video} target="_blank" rel="noreferrer" className="h-20 w-20 rounded-xl border-2 border-border grid place-items-center bg-ink text-white"><Play size={20} /></a>
            )}
          </div>
        </div>

        <div>
          <Link to={`/merchant/${p.merchant_slug}`} className="inline-flex items-center gap-1.5 text-sm text-emerald font-semibold"><Store size={15} /> {p.brand}</Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-2">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <Stars rating={p.rating} size={16} /><span className="text-sm text-muted-foreground">{p.rating} · {p.reviews} reviews</span>
            <LiveViewers />
          </div>

          <div className="flex items-end gap-3 mt-5">
            {p.price_on_request ? (
              <span className="font-display font-extrabold text-2xl text-coral">Request Current Price</span>
            ) : (
              <>
                <Price amount={p.price} size="lg" testid="pd-price" />
                {p.prev_price > p.price && <Price amount={p.prev_price} size="sm" strike className="mb-1" />}
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2"><AvailabilityBadge status={p.availability_status} />{p.price_on_request ? "Supplier price confirmed by the SHOP NOW quote desk before you pay." : "Price includes 15% GCT. Financing available at checkout."}</p>

          {p.price_breakdown && (
            <div className="mt-3 rounded-2xl border border-border bg-secondary/40 p-4 text-xs" data-testid="price-breakdown-box">
              <span className="font-bold uppercase tracking-wider text-ink">How this price is built</span>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><p className="text-muted-foreground">Supplier cost</p><p className="font-semibold">{jmd(p.price_breakdown.supplier_cost)}</p></div>
                <div><p className="text-muted-foreground">+ GCT {p.price_breakdown.gct_pct}%</p><p className="font-semibold">{jmd(p.price_breakdown.cost_incl_gct)}</p></div>
                <div><p className="text-muted-foreground">+ Markup {p.price_breakdown.markup_pct}%</p><p className="font-extrabold text-ink">{jmd(p.price_breakdown.sell_price)}</p></div>
              </div>
            </div>
          )}
          {p.volume_pricing?.length > 0 && (
            <div className="mt-4 rounded-2xl border border-emerald/30 bg-emerald/5 p-4" data-testid="volume-pricing-box">
              <span className="text-xs font-bold uppercase tracking-wider text-ink">Case pricing · buy more, pay less per case</span>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {p.volume_pricing.map((t) => (
                  <div key={t.qty} className="bg-white rounded-xl p-3" data-testid={`volume-tier-${t.min_qty}`}>
                    <p className="text-[11px] text-muted-foreground">{t.qty}</p>
                    <p className="font-display font-extrabold text-lg text-ink">{jmd(t.price)}</p>
                    <p className="text-[11px] text-muted-foreground">per case</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">The lower case price is applied automatically at checkout based on the quantity in your cart.</p>
            </div>
          )}

          {p.trade_pricing?.length > 0 && (
            <div className="mt-4 rounded-2xl border border-ink/15 bg-secondary/50 p-4" data-testid="trade-pricing-box">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-ink">Trade pricing · business buyers</span>
                <span className="text-[10px] bg-ink text-white rounded-full px-2 py-0.5">Wholesale</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {p.trade_pricing.map((t) => (
                  <div key={t.qty} className="bg-white rounded-xl p-3" data-testid={`trade-tier-${t.qty.replace(/\s+/g, "-").toLowerCase()}`}>
                    <p className="text-xs text-muted-foreground">{t.qty === "Over 1000" ? "1000+ units" : `${t.qty.replace(" to ", "–")} units`}</p>
                    <p className="font-display font-extrabold text-xl text-ink">{jmd(t.price)}</p>
                    <p className="text-[11px] text-muted-foreground">per unit</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Volume orders are confirmed by our trade desk before payment. Ask SHOP AI or Support to place a bulk order.</p>
            </div>
          )}

          {p.age_restricted && (
            <div className="mt-4">
              <ComplianceNotice text="Tobacco product — for adults 18+ only. Tobacco use is harmful to health. A valid age check is required at delivery." />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-6 text-sm">
            <div className="flex items-center gap-2 bg-secondary/60 rounded-xl px-3 py-2.5"><MapPin size={16} className="text-emerald" /> Available near {p.parish}</div>
            <div className="flex items-center gap-2 bg-secondary/60 rounded-xl px-3 py-2.5"><Truck size={16} className="text-emerald" /> {p.delivery_days}-day delivery</div>
            <div className="flex items-center gap-2 bg-secondary/60 rounded-xl px-3 py-2.5">
              <Package size={16} className={p.stock > 0 && p.stock <= 8 ? "text-coral" : "text-emerald"} />
              {p.stock <= 0 ? "Out of stock" : p.stock <= 8 ? <span className="text-coral font-semibold" data-testid="pdp-low-stock">Only {p.stock} left!</span> : `${p.stock} in stock`}
            </div>
            <div className="flex items-center gap-2 bg-secondary/60 rounded-xl px-3 py-2.5"><Store size={16} className="text-emerald" /> Ships from {p.warehouse} Warehouse</div>
          </div>

          {p.stock <= 0 && (
            <button onClick={handleRestock} data-testid="restock-alert-btn"
              className="mt-4 w-full flex items-center justify-center gap-2 border border-coral text-coral rounded-xl py-3 font-semibold hover:bg-coral hover:text-white transition-colors">
              <Bell size={17} /> Notify me when it's back
            </button>
          )}

          <div className="flex items-center gap-3 mt-6">
            {p.price_on_request ? (
              <>
                <button data-testid="pdp-request-price" onClick={() => setEnquireOpen(true)}
                  className="flex-1 flex items-center justify-center gap-2 bg-coral text-white rounded-xl py-3.5 font-semibold hover:bg-ink transition-colors">
                  <ShoppingCart size={18} /> Request Price / Enquire
                </button>
                <button onClick={() => toggleWish(p)} className="h-12 w-12 grid place-items-center rounded-xl border border-border hover:border-coral transition-colors">
                  <Heart size={20} className={inWishlist(p.id) ? "fill-coral text-coral" : ""} />
                </button>
                <button data-testid="pdp-compare-req" onClick={() => toggleCompare(p)} title="Compare"
                  className={`h-12 w-12 grid place-items-center rounded-xl border transition-colors ${inCompare(p.id) ? "bg-ink text-white border-ink" : "border-border hover:border-ink"}`}>
                  <Scale size={20} />
                </button>
              </>
            ) : (
              <>
                  <QtyStepper value={qty} onChange={setQty} id="pdp-qty" unit={p.unit} />
                <button data-testid="pdp-add-to-cart" disabled={qty < 1} onClick={() => addToCart(p, qty)} className="flex-1 flex items-center justify-center gap-2 border border-ink/15 rounded-xl py-3 font-semibold hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <ShoppingCart size={18} /> {qty < 1 ? "Select quantity" : `Add ${qty} to Cart`}
                </button>
                <button onClick={() => toggleWish(p)} className="h-12 w-12 grid place-items-center rounded-xl border border-border hover:border-coral transition-colors">
                  <Heart size={20} className={inWishlist(p.id) ? "fill-coral text-coral" : ""} />
                </button>
                <button data-testid="pdp-compare" onClick={() => toggleCompare(p)} title="Compare"
                  className={`h-12 w-12 grid place-items-center rounded-xl border transition-colors ${inCompare(p.id) ? "bg-ink text-white border-ink" : "border-border hover:border-ink"}`}>
                  <Scale size={20} />
                </button>
              </>
            )}
          </div>
          {p.dept === "fine_arts" && p.availability === "sourced_from_cuba" && !p.price_on_request && (
            <ArtDepositButton p={p} />
          )}
          {!p.price_on_request && <WeightNote unitKg={unitWeightKg(p)} qty={qty} unit={p.unit} id="pdp-weight" />}
          {p.price_on_request && (
            <>
              <PriceRequestForm product={p} />
              <Link to={`/hardware?tab=rfq&product=${p.id}&name=${encodeURIComponent(p.name)}`} data-testid="pdp-request-quote" className="mt-2 inline-block text-xs text-muted-foreground underline">Need a multi-item or custom quotation? Open the full RFQ form</Link>
            </>
          )}
          {!p.price_on_request && (
            <button data-testid="pdp-buy-now" disabled={qty < 1} onClick={() => { addToCart(p, qty, false); navigate("/checkout"); }}
              className="w-full mt-3 flex items-center justify-center gap-2 bg-ink text-white rounded-xl py-3.5 font-semibold hover:bg-emerald-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Zap size={18} /> Buy Now
            </button>
          )}

          <div className="flex flex-wrap gap-4 mt-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald" /> Secure payment</span>
            <span className="flex items-center gap-1.5"><RotateCcw size={15} className="text-emerald" /> 7-day returns</span>
            <span className="flex items-center gap-1.5"><Check size={15} className="text-emerald" /> Verified merchant</span>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="flex gap-2 border-b border-border overflow-x-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap" data-testid="pdp-tabs">
          {[["desc", "Description"], ["specs", "Specifications"], ["research", "AI Research"], ["reviews", `Reviews (${data.reviews.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); if (k === "research") runResearch(); }} data-testid={`pdp-tab-${k}`} className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-emerald text-emerald" : "border-transparent text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        <div className="py-6">
          {tab === "desc" && <p className="text-slate-700 leading-relaxed max-w-3xl">{p.description}</p>}
          {tab === "specs" && (
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-1 max-w-3xl">
              {Object.entries(p.specs || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between py-2.5 border-b border-border"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>
              ))}
              <div className="flex justify-between py-2.5 border-b border-border"><span className="text-muted-foreground">SKU</span><span className="font-mono-sn text-sm">{p.sku}</span></div>
            </div>
          )}
          {tab === "research" && (
            <div className="max-w-3xl" data-testid="pdp-research">
              {researchBusy && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Researching with AI…</div>}
              {researchErr && <div className="text-sm bg-secondary rounded-xl p-4 flex items-start gap-2" data-testid="pdp-research-unavailable"><Sparkles size={16} className="text-emerald mt-0.5" /> {researchErr}</div>}
              {research && (
                <div className="space-y-4">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{research.answer}</p>
                  {research.citations?.length > 0 && (
                    <div>
                      <h4 className="font-display font-semibold mb-1 text-sm">Sources</h4>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {research.citations.map((u) => <li key={u}><a href={u} target="_blank" rel="noreferrer" className="text-emerald underline break-all">{u}</a></li>)}
                      </ul>
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground">AI-generated from live web sources. Prices &amp; stock on this page are authoritative.</p>
                </div>
              )}
            </div>
          )}
          {tab === "reviews" && (
            <div className="max-w-3xl space-y-6">
              <div className="bg-secondary/50 rounded-2xl p-5">
                <h4 className="font-display font-semibold mb-3">Write a review</h4>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => <button key={i} onClick={() => setRating(i)}><span className={i <= rating ? "text-gold text-2xl" : "text-slate-300 text-2xl"}>★</span></button>)}
                </div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="w-full rounded-xl border border-border p-3 text-sm" rows={3} data-testid="review-input" />
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <label className="flex items-center gap-2 text-xs font-semibold text-ink cursor-pointer bg-white border border-border rounded-xl px-3 py-2 hover:bg-secondary" data-testid="review-video-label">
                    {upBusy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} className="text-emerald" />}
                    {reviewVideo ? "Video attached ✓" : "Add a video review"}
                    <input type="file" accept="video/*" onChange={uploadReviewVideo} className="hidden" data-testid="review-video-input" />
                  </label>
                  {reviewVideo && <button onClick={() => setReviewVideo("")} className="text-xs text-coral font-semibold">Remove</button>}
                  <button onClick={submitReview} className="ml-auto bg-ink text-white rounded-xl px-5 py-2.5 text-sm font-semibold" data-testid="review-submit">Submit Review</button>
                </div>
              </div>
              {data.reviews.length === 0 && <p className="text-muted-foreground">No reviews yet. Be the first!</p>}
              {data.reviews.map((r) => (
                <div key={r.id} className="border-b border-border pb-4">
                  <div className="flex items-center gap-2"><Stars rating={r.rating} /><span className="font-semibold text-sm">{r.author}</span>{r.verified && <span className="text-[10px] bg-emerald/10 text-emerald px-2 py-0.5 rounded-full font-semibold">Verified</span>}</div>
                  <p className="text-slate-700 text-sm mt-1">{r.comment}</p>
                  {r.video_url && <video src={assetUrl(r.video_url) + "#t=0.5"} preload="metadata" controls playsInline className="mt-2 rounded-xl w-full max-w-sm border border-border" data-testid={`review-video-${r.id}`} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-5">Frequently Bought Together & Related</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}

      {enquireOpen && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/80 backdrop-blur-md px-4" data-testid="enquiry-modal"
          onClick={(e) => { if (e.target === e.currentTarget) { setEnquireOpen(false); setEnqSent(false); } }}>
          <div className="max-w-md w-full bg-white rounded-3xl border border-border p-7 sn-fade-up">
            {enqSent ? (
              <div className="text-center py-4">
                <span className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-emerald/10 text-emerald mb-4"><Check size={28} /></span>
                <h3 className="font-display font-bold text-xl">Enquiry received</h3>
                <p className="text-muted-foreground text-sm mt-2">Thanks {enq.name.split(" ")[0]}! Our brokers will contact you at {enq.email} to confirm pricing and arrange a viewing.</p>
                <button data-testid="enquiry-close-btn" onClick={() => { setEnquireOpen(false); setEnqSent(false); }} className="mt-5 w-full bg-ink text-white rounded-full py-3 font-semibold">Done</button>
              </div>
            ) : (
              <form onSubmit={submitEnquiry}>
                <h3 className="font-display font-bold text-xl">Enquire about {p.name}</h3>
                <p className="text-muted-foreground text-sm mt-1">Leave your details and preferred viewing date — our brokers confirm current pricing and arrange a private viewing.</p>
                <div className="space-y-3 mt-5">
                  <input data-testid="enq-name" value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} placeholder="Full name" className="w-full border border-border rounded-xl px-4 py-3 text-sm" />
                  <input data-testid="enq-email" type="email" value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} placeholder="Email" className="w-full border border-border rounded-xl px-4 py-3 text-sm" />
                  <input data-testid="enq-phone" value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} placeholder="Phone" className="w-full border border-border rounded-xl px-4 py-3 text-sm" />
                  <label className="block text-xs text-muted-foreground font-semibold">Preferred viewing date
                    <input data-testid="enq-date" type="date" value={enq.viewing_date} onChange={(e) => setEnq({ ...enq, viewing_date: e.target.value })} className="w-full border border-border rounded-xl px-4 py-3 text-sm mt-1 font-normal text-ink" />
                  </label>
                  <textarea data-testid="enq-message" value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} placeholder="Anything specific you'd like to know?" rows={2} className="w-full border border-border rounded-xl px-4 py-3 text-sm" />
                </div>
                <div className="flex gap-2 mt-5">
                  <button type="button" onClick={() => setEnquireOpen(false)} className="flex-1 border border-border rounded-full py-3 font-semibold text-sm">Cancel</button>
                  <button type="submit" data-testid="enq-submit-btn" className="flex-1 bg-coral text-white rounded-full py-3 font-semibold text-sm hover:bg-ink transition-colors">Send Enquiry</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      <YouMightLike category={p.category} excludeId={id} />
      <RecentlyViewed excludeId={id} />
    </div>
  );
}
